import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'

/**
 * What the chat dock shows for a project that did not open.
 *
 * Browser finding, 2026-08-13 (F13,
 * docs/studio/chat-ux-sweep-2026-08-13-ide-chat-surfaces.md): on a load failure
 * the WHOLE chat dock was removed — `document.querySelector(
 * '[data-responsive-panel="chat"]')` was `null` — so the one surface that could
 * explain the failure disappeared at the moment it was needed, while the top
 * bar kept its controls. ChatRail's `hasProjectConversation` gate
 * (`currentProjectId || currentProject`, both null after a failed open) is what
 * removed it.
 *
 * Two properties this panel exists to hold, in this order:
 *
 *   1. INFORMED — it names what happened, in the session's language. The raw
 *      transport reason ("Not found", English in every locale) stays on the
 *      canvas where the engineer-readable detail belongs; this surface is the
 *      creator's, so it carries authored copy only.
 *   2. UNABLE TO ACT ON NOTHING — there is deliberately NO composer here. A
 *      message sent against a project that does not exist can only fail, and
 *      the whole point of the finding it closes is that a surface must not
 *      offer an action it cannot perform. The one control is the way out, the
 *      same one the canvas offers.
 */
export function ProjectLoadErrorChatPanel() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const closeProject = useStudioStore((s) => s.closeProject)

  return (
    <div
      data-testid="chat-project-load-error"
      role="status"
      className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto border-l border-border bg-background p-4"
    >
      <h2 className="text-sm font-semibold text-foreground">
        {copy.couldNotLoadProject}
      </h2>
      <p className="text-xs leading-5 text-muted-foreground">
        {copy.projectNotOpenNothingToChange}
      </p>
      <button
        type="button"
        data-testid="chat-project-load-error-back"
        onClick={() => closeProject()}
        className="self-start rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copy.backToProjects}
      </button>
    </div>
  )
}
